import { inject, Injectable, signal } from '@angular/core';

import {
  finalize,
  Observable,
  tap,
} from 'rxjs';
import {
  AiChatContext,
  GetUserContextsRes,
  WorkContextOption,
} from '@fb/models';

import { AI_CHAT_DEFAULT_CONTEXT, aiChatShellPath } from './ai-chat.constants';
import { AiChatFormService } from './ai-chat-form.service';
import {
  contextFromShellPathString,
  persistLastChatContext,
  persistLastChatContextFromNavigatePath,
  readStoredLastChatContext,
} from './ai-chat-last-tab.storage';

export const PT_AI_CHAT_NAVIGATE_EVENT = 'pt-ai-chat-navigate';

export type PtAiChatNavigateDetail = { path: string };

export function readAiChatContextFromBrowserPath(): AiChatContext | null {
  if (typeof window === 'undefined') {
    return null;
  }

  return contextFromShellPathString(window.location.pathname ?? '');
}

/** Full-page context tabs + host URL sync + For Work context options (not used in popup). */
@Injectable()
export class AiChatTabBridgeService {
  readonly #service = inject(AiChatFormService);

  readonly activeContext = signal<AiChatContext>(
    readAiChatContextFromBrowserPath() ?? AI_CHAT_DEFAULT_CONTEXT,
  );
  readonly allowedContexts = signal<AiChatContext[]>([AiChatContext.Personal]);
  /** Lowercased `WORK.context.role` from tabs API (`ta` | `hr` | `support`); null if absent. */
  readonly forWorkRole = signal<string | null>(null);
  /**
   * `WORK.workContexts` from `/contexts`, in response order; empty — Work chats are created
   * without a picker.
   */
  readonly workContexts = signal<WorkContextOption[]>([]);
  readonly tabsInitError = signal<unknown | null>(null);

  /**
   * True once the tabs API response has been applied (`allowedContexts` is authoritative).
   * Used so `syncFromHost` cannot restore a context the user is not allowed to use.
   */
  readonly tabBootstrapComplete = signal(false);

  /** Sync active context from host route input. */
  syncFromHost(tab: string | null | undefined): void {
    let next: AiChatContext =
      tab === AiChatContext.Work ? AiChatContext.Work : AiChatContext.Personal;

    const allowed = this.allowedContexts();

    if (this.tabBootstrapComplete() && !allowed.includes(next)) {
      next = allowed.includes(AiChatContext.Personal)
        ? AiChatContext.Personal
        : (allowed[0] ?? AiChatContext.Personal);
    }

    this.activeContext.set(next);

    if (this.tabBootstrapComplete()) {
      persistLastChatContext(next);
    }
  }

  loadTabsAndLicenses(options: { disableNavigation?: boolean } = {}): Observable<GetUserContextsRes> {
    const disableNavigation = options.disableNavigation ?? false;

    this.tabsInitError.set(null);

    return this.#service.getUserContexts().pipe(
      tap({
        next: res => {
          const normalized = this.#normalizedContextsFromResponse(res);
          this.allowedContexts.set(normalized);

          const workContext = res.find(
            contextItem => contextItem.id === AiChatContext.Work,
          );
          this.forWorkRole.set(workContext?.role?.toLowerCase() ?? null);
          this.workContexts.set(workContext?.workContexts ?? []);

          if (!normalized.includes(this.activeContext())) {
            this.activeContext.set(AiChatContext.Personal);
            if (!disableNavigation) {
              this.#emitNavigate(aiChatShellPath(AiChatContext.Personal));
            } else {
              persistLastChatContext(AiChatContext.Personal);
            }
          } else if (
            this.activeContext() === AiChatContext.Personal
            && readStoredLastChatContext() === AiChatContext.Work
            && normalized.includes(AiChatContext.Work)
          ) {
            if (!disableNavigation) {
              this.#emitNavigate(aiChatShellPath(AiChatContext.Work));
            } else {
              this.activeContext.set(AiChatContext.Work);
            }
          }

          this.tabBootstrapComplete.set(true);
        },
        error: err => {
          this.tabsInitError.set(err);
          this.forWorkRole.set(null);
          this.workContexts.set([]);

          if (this.activeContext() === AiChatContext.Work) {
            this.activeContext.set(AiChatContext.Personal);
            if (!disableNavigation) {
              this.#emitNavigate(aiChatShellPath(AiChatContext.Personal));
            } else {
              persistLastChatContext(AiChatContext.Personal);
            }
          }

          this.tabBootstrapComplete.set(true);
        },
      }),
      finalize(() => {
        if (!this.tabBootstrapComplete()) {
          this.tabBootstrapComplete.set(true);
        }
      }),
    );
  }

  #normalizedContextsFromResponse(res: GetUserContextsRes): AiChatContext[] {
    const contexts: AiChatContext[] = [];

    for (const contextItem of (res ?? [])) {
      if (contextItem.id === AiChatContext.Personal) {
        if (!contexts.includes(AiChatContext.Personal)) {
          contexts.push(AiChatContext.Personal);
        }
      } else if (contextItem.id === AiChatContext.Work) {
        if (!contexts.includes(AiChatContext.Work)) {
          contexts.push(AiChatContext.Work);
        }
      }
    }

    return contexts.length > 0 ? contexts : [AiChatContext.Personal];
  }

  requestNavigateToContext(context: AiChatContext): void {
    this.activeContext.set(context);

    this.#emitNavigate(aiChatShellPath(context));
  }

  /**
   * Switch context without emitting a URL-navigation event.
   * Used in popup (dialog) mode where the host app must NOT be asked to navigate.
   */
  switchContextInPlace(context: AiChatContext): void {
    this.activeContext.set(context);
    persistLastChatContext(context);
  }

  #emitNavigate(path: string): void {
    persistLastChatContextFromNavigatePath(path);

    window.dispatchEvent(
      new CustomEvent<PtAiChatNavigateDetail>(PT_AI_CHAT_NAVIGATE_EVENT, {
        detail: { path },
        bubbles: true,
        composed: true,
      }),
    );
  }
}
